import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/stores/hook";
import type { AIToolCallRecord } from "../types/ai.types";
import { applyAutomationDraft, draftOf } from "../automationDraft/applyAutomationDraft";

// ---------------------------------------------------------------------------
// AutomationDraftCards — the rule the assistant drafted, in words, under the
// answer (same idea as ChartCards). The draft already built itself in the
// builder when the tool result arrived; "Open in builder" replays it (the
// user navigated away, or cancelled the form). It is a DRAFT until they Save.
// ---------------------------------------------------------------------------

export default function AutomationDraftCards({ toolCalls }: { toolCalls: AIToolCallRecord[] }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Redrafts in one turn: only the last one is the rule on screen.
  const last = [...toolCalls].reverse().find((t) => draftOf(t));
  const draft = last ? draftOf(last) : null;
  if (!last || !draft) return null;
  const words = (last.result?.inWords ?? {}) as { onlyIf?: string; runs?: string };

  return (
    <div style={card}>
      <div style={head}>
        <span style={badge}>Draft · not saved</span>
        <span style={title}>{draft.name}</span>
      </div>
      <Row label="When" value={draft.trigger.label ?? draft.trigger.event} />
      {words.onlyIf && <Row label="Only if" value={words.onlyIf} />}
      <Row
        label="Then"
        value={draft.actions.map((a, i) => `${i + 1}. ${a.label ?? a.type}`).join("  ")}
      />
      {words.runs && words.runs !== "any time" && <Row label="Runs" value={words.runs} />}
      <div style={foot}>
        <span style={hint}>Review it in the builder, then press Save.</span>
        <button
          type="button"
          style={button}
          onClick={() => void applyAutomationDraft(draft, dispatch, navigate)}
        >
          Open in builder
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={row}>
      <span style={rowLabel}>{label}</span>
      <span style={rowValue}>{value}</span>
    </div>
  );
}

const card: CSSProperties = {
  border: "1px solid #cfe5dc",
  borderRadius: 10,
  padding: "10px 12px",
  margin: "6px 0",
  background: "#f4faf7",
  width: "100%",
  minWidth: 260,
  boxSizing: "border-box",
};
const head: CSSProperties = { display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" };
const badge: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.3,
  textTransform: "uppercase",
  color: "#156548",
  background: "#dcefe7",
  borderRadius: 999,
  padding: "2px 8px",
};
const title: CSSProperties = { fontSize: 13, fontWeight: 600, color: "#051912" };
const row: CSSProperties = { display: "flex", gap: 8, fontSize: 12, lineHeight: 1.45, marginBottom: 4 };
const rowLabel: CSSProperties = { flex: "0 0 52px", color: "#6b7280" };
const rowValue: CSSProperties = { color: "#1f2937", wordBreak: "break-word" };
const foot: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 8 };
const hint: CSSProperties = { fontSize: 11, color: "#6b7280" };
const button: CSSProperties = {
  border: "none",
  borderRadius: 8,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 600,
  color: "#fff",
  background: "#156548",
  cursor: "pointer",
  whiteSpace: "nowrap",
};
